import * as React from 'react';
import Pagination from '../Pagination';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { useSelector } from 'react-redux';
import { RootState } from '@/types';
import FeeSummaryCard from '../FeeSummaryCard';
import { useGetAllFeeBYMonthQuery } from '@/store/feeApi';

interface FeeRecord {
  lastRecordDate: string;
  name: string;
  bCode: string;
  course: string;
  paid: number;
  payable: number;
  student: string;
  formattedDate?: string;
  formattedTime?: string;
  formattedDateTime?: string;
  displayPeriod?: string;
}

export default function ViewFee({
  fees,
  studentName,
  selectedDate,
  selectedMonth,
  selectedYear,
}) {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.user);

  // Fetch all individual payment records to calculate recent paid amounts
  const { data: allFeeRecords } = useGetAllFeeBYMonthQuery(user?.token);

  const recentPaidAmounts = React.useMemo(() => {
    if (!allFeeRecords || allFeeRecords.length === 0) {
      return {};
    }

    const studentAmounts: Record<string, number> = {};

    // Helper to normalize student ID safely
    const getStudentId = (student: any): string => {
      if (!student) return '';
      if (typeof student === 'string') return student;
      if (student._id) return student._id;
      if (typeof student.toHexString === 'function') return student.toHexString();
      const str = student.toString();
      const match = str.match(/ObjectId\("([a-f0-9]{24})"\)/);
      return match ? match[1] : str;
    };

    // Case 1: Filter is applied → sum payments in selected date/month/year
    if (selectedDate || selectedMonth || selectedYear) {
      let selectedMonthFromDate: string | null = null;
      let selectedYearFromDate: string | null = null;

      if (selectedDate) {
        const [day, month, year] = selectedDate.split('/');
        if (day && month && year) {
          const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          selectedMonthFromDate = dateObj.toLocaleString('en-US', { month: 'long' });
          selectedYearFromDate = year;
        }
      }

      const monthToMatch = selectedDate && selectedMonthFromDate ? selectedMonthFromDate : selectedMonth;
      const yearToMatch = selectedDate && selectedYearFromDate ? selectedYearFromDate : selectedYear;

      allFeeRecords.forEach((payment: any) => {
        if (!payment.date || !payment.paid) return;

        const paymentDate = new Date(payment.date);
        if (isNaN(paymentDate.getTime())) return;

        const paymentDateStr = paymentDate.toLocaleDateString('en-GB');
        const paymentMonth = paymentDate.toLocaleString('en-US', { month: 'long' });
        const paymentYear = paymentDate.getFullYear().toString();

        const matchesDate = selectedDate ? paymentDateStr === selectedDate : true;
        const matchesMonth = monthToMatch ? paymentMonth === monthToMatch : true;
        const matchesYear = yearToMatch ? paymentYear === yearToMatch : true;

        if (matchesDate && matchesMonth && matchesYear) {
          const studentId = getStudentId(payment.student);
          if (!studentId) return;

          const amount = Number(payment.paid) || 0;
          studentAmounts[studentId] = (studentAmounts[studentId] || 0) + amount;
        }
      });

      return studentAmounts;
    }

    // Case 2: NO filter → get the MOST RECENT (latest) payment for each student
    // First, group all payments by student and sort by date descending
    const paymentsByStudent: Record<string, any[]> = {};

    allFeeRecords.forEach((payment: any) => {
      const studentId = getStudentId(payment.student);
      if (!studentId || !payment.date || !payment.paid) return;

      const paymentDate = new Date(payment.date);
      if (isNaN(paymentDate.getTime())) return;

      if (!paymentsByStudent[studentId]) {
        paymentsByStudent[studentId] = [];
      }
      paymentsByStudent[studentId].push({
        ...payment,
        parsedDate: paymentDate,
      });
    });

    // For each student, sort by date desc and pick the latest payment amount
    Object.keys(paymentsByStudent).forEach((studentId) => {
      const payments = paymentsByStudent[studentId];
      payments.sort((a, b) => b.parsedDate.getTime() - a.parsedDate.getTime());
      const latestPayment = payments[0];
      studentAmounts[studentId] = Number(latestPayment.paid) || 0;
    });

    return studentAmounts;
  }, [allFeeRecords, selectedDate, selectedMonth, selectedYear]);

  const filteredFees = React.useMemo(() => {
    const studentsWithPayments = new Set(Object.keys(recentPaidAmounts));
    const hasDateFilter = !!(selectedDate || selectedMonth || selectedYear);

    // Determine what text to show in the "Date" column
    let displayDateLabel = '';
    if (selectedDate) {
      displayDateLabel = `On ${selectedDate}`;
    } else if (selectedMonth && selectedYear) {
      displayDateLabel = `In ${selectedMonth} ${selectedYear}`;
    } else if (selectedYear) {
      displayDateLabel = `In ${selectedYear}`;
    }

    return (fees ?? [])
      .map((record: FeeRecord) => {
        const recordDate = new Date(record.lastRecordDate);
        return {
          ...record,
          formattedDate: recordDate.toLocaleDateString('en-GB'),
          formattedTime: recordDate.toLocaleTimeString('en-GB'),
          formattedDateTime: recordDate.toLocaleString('en-GB', {
            dateStyle: 'short',
            timeStyle: 'medium',
          }),
          // Add this new field
          displayPeriod: displayDateLabel || recordDate.toLocaleDateString('en-GB'),
        };
      })
      .filter((record: FeeRecord) => {
        const matchesName = studentName
          ? record.name.toLowerCase().includes(studentName.toLowerCase())
          : true;

        if (!hasDateFilter) {
          return matchesName;
        }

        const studentId = record.student?.toString() || record.student;
        const hasRecentPayment = studentsWithPayments.has(studentId);

        return matchesName && hasRecentPayment;
      })
      .sort((a, b) => {
        // Keep sorting by lastRecordDate or switch to something else if needed
        return new Date(b.lastRecordDate).getTime() - new Date(a.lastRecordDate).getTime();
      });
  }, [fees, studentName, selectedDate, selectedMonth, selectedYear, recentPaidAmounts]);

  const totalAmounts = React.useMemo(() => {
    const hasDateFilter = !!(selectedDate || selectedMonth || selectedYear);
    return filteredFees.reduce(
      (acc, record) => {
        const studentId = record.student?.toString() || record.student;
        const paidForPeriod = hasDateFilter
          ? recentPaidAmounts[studentId] || 0
          : record.paid;

        acc.paid += paidForPeriod;
        acc.payable += record.payable;
        return acc;
      },
      { paid: 0, payable: 0 }
    );
  }, [filteredFees, recentPaidAmounts, selectedDate, selectedMonth, selectedYear]);

  console.log("filteredFees: ", filteredFees);
  console.log("recentPaidAmounts: ", recentPaidAmounts);

  return (
    <Pagination data={filteredFees} itemsPerPage={10}>
      {(paginatedData) => (
        <>
          <div className="rounded-lg border bg-card">
            <table className="text-sm text-left overflow-x-scroll">
              <thead>
                <tr className="bg-muted border-b text-foreground">
                  <th className="px-6 py-4 font-semibold">No.</th>
                  <th className="px-6 py-4 font-semibold">Date</th>
                  <th className="px-6 py-4 font-semibold">Student Name</th>
                  <th className="px-6 py-4 font-semibold">Batch Code</th>
                  <th className="px-6 py-4 font-semibold">Course Name</th>
                  <th className="px-6 py-4 font-semibold text-right">
                    Paid Amount
                  </th>
                  <th className="px-6 py-4 font-semibold text-right">
                    Recent Paid Amount
                  </th>
                  <th className="px-6 py-4 font-semibold text-right">
                    Payable Amount
                  </th>
                  <th className="px-6 py-4 font-semibold text-right">
                    Pending Amount
                  </th>
                  {/* <th className="px-6 py-4 font-semibold">Action</th> */}
                </tr>
              </thead>
              <tbody>
                {paginatedData?.map((record: FeeRecord, index) => (
                  <tr
                    key={index}
                    className="border-b hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => { navigate(`/feehistory/${record?.student}`) }}
                  >
                    <td className="px-6 py-4 text-foreground ">{index + 1}</td>
                    <td className="px-6 py-4 text-foreground font-medium">
                      {record.displayPeriod || record.formattedDate}
                    </td>
                    <td className="px-6 py-4 font-medium text-foreground">
                      {record.name}
                    </td>
                    <td className="px-6 py-4 text-foreground">
                      {record.bCode}
                    </td>
                    <td className="px-6 py-4 text-foreground">
                      {record.course}
                    </td>
                    <td className="px-6 py-4 text-right text-[#26BDB0]">
                      {(() => {
                        const hasDateFilter = !!(selectedDate || selectedMonth || selectedYear);
                        const studentId = record.student?.toString() || record.student;
                        const paidForPeriod = hasDateFilter
                          ? recentPaidAmounts[studentId] || 0
                          : record.paid;
                        return `₹${paidForPeriod.toLocaleString()}`;
                      })()}
                      {/* ₹{record.paid.toLocaleString()} */}
                    </td>
                    <td className="px-6 py-4 text-right text-purple-600">
                      ₹{(() => {
                        // Normalize student ID for matching (handle both ObjectId and string)
                        const studentId = record.student?.toString() || record.student;
                        console.log("studentId", studentId);
                        const amount = recentPaidAmounts[studentId] || 0;
                        return amount.toLocaleString();
                      })()}
                    </td>
                    <td className="px-6 py-4 text-right text-foreground">
                      ₹{record.payable.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right text-red-600">
                      {(() => {
                        const hasDateFilter = !!(selectedDate || selectedMonth || selectedYear);
                        const studentId = record.student?.toString() || record.student;
                        const paidForPeriod = hasDateFilter
                          ? recentPaidAmounts[studentId] || 0
                          : record.paid;
                        return `₹${(record.payable - paidForPeriod).toLocaleString()}`;
                      })()}
                    </td>
                    {/* <td className="px-6 py-4">
                      <Link to={`/feehistory/${record?.student}`}>
                        <Button
                          variant="link"
                          className="text-blue-500 hover:text-blue-700 p-0"
                        >
                          View History
                        </Button>
                      </Link>
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {user?.role === 'admin' && (
            <FeeSummaryCard
              pendingFees={totalAmounts.payable - totalAmounts.paid}
              receivedFees={totalAmounts.paid}
            />
          )}
        </>
      )}
    </Pagination>
  );
}
